import React, { useRef, useState, useEffect } from 'react';
import _ from 'lodash';
import './chart.css';
import { decimalPrecision, addProperties, getSelectedAreaMap } from '../util.js';
import useResizeObserver from '../useResizeObserver.js';
import { jsonPath } from '../contents/jsonPath';
import { geoMercator, format, geoPath, scaleQuantize, select, text, scaleThreshold } from 'd3';
import { feature } from 'topojson';
import SideChartNav from './SideChartNav';
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { legendColor, legendHelpers } from 'd3-svg-legend';
import $ from 'jquery';

export const MapHolder = ({ barChartData, areaType, area, district, sub_district, indicatorType, indicatorVariant, areaDetails, minVal, maxVal, toggleValue, colorMode }) => {

  let fyID = barChartData[0].financial_year.fy_id;
  let fyName = barChartData[0].financial_year.fy_name;
  let textLabel, TextValue = null;

  let mhMap;

  if (areaType === 'District' && fyID >= 4) {
    mhMap = jsonPath.filter(f => f.type === 'DtwithMS')[0].path;
  } else {
    mhMap = jsonPath.filter(f => f.type === areaType)[0].path;
  }

  const indicatorName = barChartData[0].indicator.label;
  const indicatorId = barChartData[0].indicator.indicator_id;

  let mapTitle = `${indicatorName}, FY: ${fyName}, Month:  ${barChartData[0].month.month_name}`;

  let districtObject = null;
  if (areaType === 'District' && fyID < 4) {
    districtObject = mhMap.objects.mh_dtshp_with_bmumbai25052022;
  }
  else if (areaType === 'SubDistrict')
    districtObject = mhMap.objects.mh_sub_dt_equi2hmis071221;
  else if (areaType === 'District') {
    districtObject = mhMap.objects.mh_dtlevel_hmisequi24052022;
  }

  let features = feature(mhMap, districtObject).features;

  if (areaType !== 'District')
    features = getSelectedAreaMap(features, area, null, null, areaType, areaDetails);

  let geometry = { type: "FeatureCollection", features }
  let geojson = geometry.features;
  geojson = _.map(geojson, object => {
    return _.omit(object, ['dataValue'])
  });

  TextValue = d => d.areaname;

  let mergedGeometry = addProperties(geojson, barChartData, areaType);

  const palettes = {
    default: {
      positive: ["#FF0000", "#FFFF00", "#30A054", "#006837"],
      negative: ["#30A054", "#FFFF00", "#FF0000"]
    },
    colorblind: {
      positive: ["#D55E00", "#E69F00", "#009B93", "#000000"],
      negative: ["#009B93", "#E69F00", "#D55E00"]
    }
  };


  let low = minVal;
  let high = maxVal;
  let highest = 100;

  let myColor = (v, low, high, palette) => {
    if (indicatorType === 'negative') {
      if (v < low) return palette.negative[0];
      else if (v >= low && v <= high) return palette.negative[1];
      else return palette.negative[2];
    } else {
      if (v < low) return palette.positive[0];
      else if (v >= low && v <= high) return palette.positive[1];
      else return palette.positive[2];
    }
  };

  function buildColourScale(mode, indicatorType, low, high, highest) {
    const palette = palettes[mode];

    let colourScale = scaleThreshold()
      .domain([low, high, highest])
      .range(palette.positive);

    if (indicatorType === 'negative') {
      colourScale = scaleThreshold()
        .domain([low, high])
        .range(palette.negative);
    }

    return colourScale;
  }


  const colourScale = buildColourScale(colorMode, indicatorType, low, high, highest);

  let windowWidth = window.screen.width;
  let windowHeight = window.screen.height;

  const svgRef = useRef();
  const wrapperRef = useRef();
  const componentRef = useRef();
  const dimensions = useResizeObserver(wrapperRef);
  let [offset] = useState(false);

  let tooltip = select("#map_tooltip").append("div")
    .attr("class", "tooltip")
    .style("opacity", 0);

  useEffect(() => {
    let left_offset, right_offset;

    const svg = select(svgRef.current);

    const legend = select(svgRef.current);
    let c2Value = d => d.dataValue;

    if (windowWidth >= 480) {
      windowWidth = windowWidth / 2;
      windowHeight = windowHeight / 2;
    } else {
      windowWidth = windowWidth + 100;
    }
    const { width, height } = { width: windowWidth, height: windowHeight };
    if (offset) {
      left_offset = 0;
      right_offset = 0;
    } else {
      left_offset = width;
      right_offset = height / 2;
    }
    const aspect = width / height;
    const adjustedHeight = Math.ceil(width / (aspect));

    svg.selectAll("*").remove();
    svg.attr("preserveAspectRatio", "xMinYMin meet")
      .attr("viewBox", `0 0 ${width} ${adjustedHeight}`)


    const onMouseMove = (event, d) => {
      if (toggleValue === 'percent') {
        if (typeof c2Value(d) != 'undefined') {
          tooltip.style("opacity", 0);
          tooltip.style("opacity", .9);
          tooltip.html("<b>" + d.areaname + "</b><br><b></b>" + "<b>" + decimalPrecision(c2Value(d)) + "</b>")
            .style("left", event.offsetX + "px")
            .style("top", event.offsetY + 20 + "px")
            .style("font-size", "0.9rem");
          tooltip.style('visibility', 'visible');
        }
        else {
          tooltip.style("opacity", 0);
          tooltip.style("opacity", .9);
          tooltip.html("<b>" + d.properties.block + "</b><br><b></b>")
            .style("left", event.offsetX + "px")
            .style("top", event.offsetY + 20 + "px")
            .style("font-size", "0.9rem");
          tooltip.style('visibility', 'visible');
        }
      } else {
        tooltip.style("opacity", 0);
        tooltip.style("opacity", .9);
        tooltip.html("<b>" + d.areaname + "</b><br><b></b>")
          .style("left", event.offsetX + "px")
          .style("top", event.offsetY + 20 + "px")
          .style("font-size", "0.9rem");
        tooltip.style('visibility', 'visible');
      }
    };

    let projection = geoMercator().fitSize([width / 1.1, adjustedHeight / 1.2], geometry);

    const pathGenerator = geoPath(projection);

    const updateColors = (mode) => {
      const palette = palettes[mode];

      let colourScale = scaleThreshold()
        .domain([low, high, highest])
        .range(palette.positive);


      if (indicatorType === 'negative') {
        colourScale = scaleThreshold()
          .domain([low, high])
          .range(palette.negative);
      }

      svg.selectAll(".polygon")
        .attr("width", width)
        .attr("height", adjustedHeight)
        .data(mergedGeometry)
        .join("path").attr("class", "polygon")
        .style("fill", d => {
          if (typeof c2Value(d) !== "undefined") {
            if (c2Value(d) >= 100) {
              return d.indicatorType === "positive"
                ? palette.positive[3]
                : palette.negative[2];
            } else {
              return myColor(c2Value(d), low, high, palette);
            }
          } else {
            return "#A9A9B0";
          }
        })
        .style("stroke", "#333")        // add boundary color
        .style("stroke-width", 0.5)
        .on("mousemove", (i, d) => onMouseMove(i, d))
        .on("mouseout", function (d) {
          tooltip
            .style("opacity", 0);
        })
        .attr("d", feature => pathGenerator(feature))
        .attr('transform', `translate(50,60)`);
    };

    updateColors(colorMode);

    if (width > 680) {
      svg.append("g")
        .attr("class", "legendQuant")
        .attr("transform", `translate(${width - (width - 10)},${adjustedHeight - 200})`)
    }
    else {
      svg.append("g")
        .attr("class", "legendQuant")
        .attr("transform", `translate(${width - (width - 10)},${adjustedHeight - 160})`)
    }

    textLabel = svg.append("g")
      .selectAll("text")
      .data(mergedGeometry)
      .enter()
      .append("text")
      .attr("x", function (d) {
        return (pathGenerator.centroid(d)[0] + 50);
      })
      .attr("y", function (d) {
        return (pathGenerator.centroid(d)[1] + 50);
      })
      .call(
        text => text.append('tspan')
          .attr("class", "text-label")
          .attr('font-size', ".625rem")
          .attr("text-anchor", "middle")
          .style('font-weight', 'bold')
          .text(d => TextValue(d))
      ).call(
        text => text.append('tspan')
          .attr("class", "text-label")
          .attr('dy', 13)
          .attr('dx', -30)
          .attr("text-anchor", "middle")

          .attr('font-size', ".625rem")
          .style('font-weight', 'bold')
          .text(d => c2Value(d))
      )


    let formatter = format(".1f");
    let myLegend;

    if (indicatorId === 9) {
      myLegend = legendColor()
        .labelFormat(formatter)
        .labels(legendHelpers.thresholdLabels)
        .title('Rate (per 1000 LB)')
        .titleWidth(180)
        .scale(colourScale);
    } else if (indicatorId === 10) {
      myLegend = legendColor()
        .labelFormat(formatter)
        .labels(legendHelpers.thresholdLabels)
        .title('Rate (per 1000 TB)')
        .titleWidth(180)
        .scale(colourScale);
    } else if ([42, 45].includes(indicatorId)) {
      myLegend = legendColor()
        .labelFormat(formatter)
        .labels(legendHelpers.thresholdLabels)
        .title('Rate (per Lakh Census 2011 pop.)')
        .titleWidth(180)
        .scale(colourScale);
    } else {
      myLegend = legendColor()
        .labelFormat(formatter)
        .labels(legendHelpers.thresholdLabels)
        .title('Percent')
        .titleWidth(180)
        .scale(colourScale);
    }

    svg.select(".legendQuant")
      .call(myLegend)

    //Map visibility for numeric data Elements
    if (null === barChartData || barChartData.length === 0 || indicatorVariant === 'Numeric' || toggleValue === 'count') {
      svg.selectAll(".text-label").remove();
      svg
        .selectAll(".polygon")
        .attr("width", width)
        .attr("height", height)
        .join("path").attr("class", "polygon")
        .style("fill", "#A9A9B0")

      svg.append("text").text("This is a numeric data element, hence map is Not Applicable")
        .style("text-anchor", "middle")
        .style("font-weight", "bold")
        .style("fill", "red")
        .attr('transform', `translate(${width / 2}, ${height / 2})`);
    };

    //Visibility for Overreporting Note
    let arrayList = barChartData.map(x => x.per_data_value);
    if (indicatorType === 'positive' && (indicatorVariant === 'Both' || indicatorVariant === 'Percent') && Math.max(...arrayList) > 100)
      $("#overreporting_note").show();
    else
      $("#overreporting_note").hide();



  }, [barChartData, dimensions, geometry, colorMode])

  const screen = useFullScreenHandle();

  let table = [];
  if (barChartData) {
    for (var i = 0; i < barChartData.length; i++) {
      table.push({
        Area: { type: String, value: barChartData[i].area.area_name },
        [`${barChartData[0].indicator.label}`]: !isNaN(barChartData[i].per_data_value) ?
          { type: Number, value: decimalPrecision(barChartData[i].per_data_value) }
          : { type: String, value: '-' }
      })

    }
  }

  const reportChange = (state, handle) => {
    if (state === true) {
      tooltip.style("opacity", 0);
      if (document.getElementById('map_tooltip').parentElement.clientWidth > 780) {
        document.getElementsByClassName("graph-title")[0].setAttribute('style', 'font-size: 1.5rem;');
        document.getElementById("overreporting_note").setAttribute('style', 'font-size: 1.2rem;');
        document.getElementById('svgMap').setAttribute('style', 'padding-top:20px');
      }
    }
    else {
      tooltip.style("opacity", 0);
      document.getElementsByClassName("graph-title")[0].setAttribute('style', 'font-size: 1rem;');
      document.getElementById("overreporting_note").setAttribute('style', 'font-size: .7rem;');
      document.getElementById('svgMap').setAttribute('style', 'padding-top:0px');

    }
  };


  return (
    <>
      <FullScreen className="my-fullscreen w-full bg-white h-full" handle={screen} onChange={reportChange}>

        <div className="relative w-full h-full" >
          <div className="block absolute w-auto max-h-max left-15 right-5" style={{ zIndex: 2 }}>
            <SideChartNav id="svgMap" screen={screen} title={mapTitle} table={table} componentRef={componentRef} />
          </div>
          <div className='relative  w-full' id="svgMap" ref={componentRef}>
            <div className="absolute right-2 left-2 md:right-5 md:left-5 mx-10 lg:mx-8 w-auto">
              <div className="graph-title text-center font-bold md:font-semibold text-sm  lg:font-bold ">{`${mapTitle}`}</div>
              <div className="text-center font-thin text-xs md:text-xs lg:text-xs" id="overreporting_note">
                Note:Above 100% has been considered green, watch-out for overreporting
              </div>
            </div>

            <div id="map_tooltip" className='align-middle w-full h-full' ref={wrapperRef}>
              <svg ref={svgRef}
                className="w-full align-center bg-white border-black border-dashed object-scale-down pt-4"></svg>

            </div>
          </div>
        </div>
      </FullScreen>
    </>
  )

}

export default MapHolder